import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useFetch } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { PageTitle, Panel, btnPrimary, btnGhost } from "./ui";
import { BrandLoader } from "../components/site/Logo";

const TONE = { VERIFIED: "bg-emerald-100 text-emerald-900", LIMITED: "bg-sky-100 text-sky-900", UNDER_REVIEW: "bg-amber-100 text-amber-950" };
const LABEL = { VERIFIED: "Provjereno", LIMITED: "Procjena s ograničenjima", UNDER_REVIEW: "U provjeri" };
const num = (x) => (x === null || x === undefined ? "—" : typeof x === "number" ? x.toLocaleString("de-DE", { maximumFractionDigits: 4 }) : String(x));

const Cases = ({ cases }) => (
  <div className="mt-3 overflow-x-auto">
    <table className="w-full text-xs">
      <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-2">Test</th><th className="pr-2 text-right">Očekivano</th><th className="pr-2 text-right">Stvarno</th><th className="pr-2 text-right">Razlika</th><th>Status</th></tr></thead>
      <tbody>{cases.map((c) => (
        <tr key={c.name} className="border-t border-border"><td className="py-1 pr-2">{c.name}{c.error ? ` (${c.error})` : ""}</td><td className="pr-2 text-right tabular-nums">{num(c.expected)}</td><td className="pr-2 text-right tabular-nums">{num(c.actual)}</td><td className="pr-2 text-right tabular-nums">{num(c.diff)}</td>
          <td className={c.passed ? "text-emerald-700" : "font-bold text-destructive"}>{c.passed ? "OK" : "GREŠKA"}</td></tr>))}</tbody>
    </table>
  </div>
);

const ToolRow = ({ t, ver, canManage, onOverride, onToggle, enabled }) => {
  const [open, setOpen] = useState(false);
  const st = ver?.status || t.status;
  return (
    <li className="py-4" data-testid={`toolcheck-${t.tool}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{t.title}</span>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${TONE[st]}`} data-testid={`toolcheck-status-${t.tool}`}>{LABEL[st]}</span>
        {!enabled && <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-semibold">isključen</span>}
        <span className="text-xs text-muted-foreground">Godina {t.reference_year} · verzija {t.version} · testova {t.cases.length} · prošlo {t.passed} · palo {t.failed}</span>
      </div>
      <p className="mt-1 text-sm">{t.method}</p>
      <p className="mt-1 text-xs text-muted-foreground">Osnova: {t.basis}{t.official && <> · <a href={t.official} target="_blank" rel="noreferrer" className="underline">službeni izvor</a></>}</p>
      <p className="mt-1 text-xs text-muted-foreground">Ograničenja: {t.limitations.join(" · ")}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button onClick={() => setOpen(!open)} className={btnGhost} data-testid={`toolcheck-cases-${t.tool}`}>{open ? "Sakrij testove" : "Prikaži testove"}</button>
        {canManage && <button onClick={() => onOverride(t.tool, st !== "UNDER_REVIEW")} className={btnGhost} data-testid={`toolcheck-override-${t.tool}`}>{st === "UNDER_REVIEW" ? "Ukloni oznaku „U provjeri“" : "Označi „U provjeri“"}</button>}
        {canManage && <button onClick={() => onToggle(t.tool, !enabled)} className={btnGhost} data-testid={`toolcheck-toggle-${t.tool}`}>{enabled ? "Isključi kalkulator" : "Uključi kalkulator"}</button>}
      </div>
      {open && (t.cases.length ? <Cases cases={t.cases} /> : <p className="mt-2 text-xs text-muted-foreground">Nema automatskih serverskih testova (računa se u pregledniku).</p>)}
    </li>
  );
};

export default function ToolChecks() {
  const { data, reload } = useFetch("/admin/tools/validation");
  const tools = useFetch("/admin/tools");
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!data || !tools.data) return <BrandLoader />;
  const enabled = Object.fromEntries(tools.data.tools.map((t) => [t.slug, t.enabled]));
  const run = async () => { setBusy(true); try { await api.post("/admin/tools/validation"); toast.success("Provjera završena"); reload(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const override = async (tool, under) => { try { await api.put("/admin/tools/validation/override", { tool, under_review: under }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const toggle = async (tool, en) => { try { await api.put("/admin/tools", { enabled: { [tool]: en } }); toast.success(en ? "Kalkulator uključen" : "Kalkulator isključen"); tools.reload(); } catch (e) { toast.error(errMsg(e)); } };
  const res = data.run?.results || [];
  return (
    <div data-testid="tool-checks-page">
      <PageTitle title="Provjere kalkulatora" sub="Testni slučajevi s očekivanim i stvarnim rezultatom. Bruto-neto se poredi sa službenim BMF programom za provjeru (PAP 2026). „Provjereno“ nije zvanična certifikacija." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {can("tools.manage") && <button onClick={run} disabled={busy} className={btnPrimary} data-testid="toolcheck-run">{busy ? "Provjeravam…" : "Pokreni provjeru"}</button>}
        <span className="text-sm text-muted-foreground" data-testid="toolcheck-last">{data.run?.at ? `Zadnja provjera: ${fmtDateTime(data.run.at)} (${data.run.by})` : "Provjera još nije pokrenuta – svi kalkulatori se prikazuju kao „U provjeri“."}</span>
        <Link to="/admin/alati" className="text-sm underline">Parametri alata</Link>
      </div>
      <Panel title="Kalkulatori" testId="toolcheck-list">
        {res.length ? <ul className="divide-y divide-border">{res.map((t) => <ToolRow key={t.tool} t={t} ver={data.verification[t.tool]} enabled={enabled[t.tool]} canManage={can("tools.manage")} onOverride={override} onToggle={toggle} />)}</ul>
          : <p className="text-sm text-muted-foreground">Nema rezultata provjere.</p>}
      </Panel>
    </div>
  );
}
